/**
 * ListSandbox — columnas, búsqueda y exportación sobre una tabla de mentira.
 *
 * Reproduce el contrato del estándar de listados: buscador → filtros →
 * Exportar → Columnas, y recordar qué columnas ves. Sin API y sin localStorage.
 */

import React, { useMemo, useState } from 'react';
import {
  AdjustmentsHorizontalIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  MagnifyingGlassIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';

const COLUMNS = [
  { key: 'name', label: 'Nombre' },
  { key: 'type', label: 'Tipo' },
  { key: 'location', label: 'Ubicación' },
  { key: 'ip', label: 'IP' },
  { key: 'state', label: 'Estado' },
  { key: 'owner', label: 'Responsable' },
];

const ROWS = [
  { id: 1, name: 'SRV-DC01', type: 'Servidor', location: 'C.P. Central · Rack A', ip: '10.10.0.11', state: 'Activo', owner: 'Infraestructura' },
  { id: 2, name: 'SRV-APP02', type: 'Servidor', location: 'C.P. Central · Rack A', ip: '10.10.0.12', state: 'Activo', owner: 'Infraestructura' },
  { id: 3, name: 'SW-CORE-01', type: 'Switch', location: 'C.P. Central · Rack B', ip: '10.10.0.2', state: 'Activo', owner: 'Redes' },
  { id: 4, name: 'FW-BORDE', type: 'Firewall', location: 'C.P. Central · Rack B', ip: '10.10.0.1', state: 'Activo', owner: 'Redes' },
  { id: 5, name: 'NAS-BACKUP', type: 'Almacenamiento', location: 'Sede Norte · Rack N1', ip: '10.20.0.30', state: 'Mantenimiento', owner: 'Sistemas' },
  { id: 6, name: 'UPS-RACK-A', type: 'SAI', location: 'C.P. Central · Rack A', ip: '10.10.0.90', state: 'Activo', owner: 'Infraestructura' },
];

export default function ListSandbox() {
  const [visible, setVisible] = useState(() => new Set(['name', 'type', 'location', 'state']));
  const [order, setOrder] = useState(COLUMNS.map((column) => column.key));
  const [panelOpen, setPanelOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [showCsv, setShowCsv] = useState(false);

  const normalized = query.trim().toLowerCase();
  const rows = useMemo(
    () => ROWS.filter((row) => !normalized || Object.values(row).join(' ').toLowerCase().includes(normalized)),
    [normalized]
  );

  const activeColumns = order
    .map((key) => COLUMNS.find((column) => column.key === key))
    .filter((column) => column && visible.has(column.key));

  const csv = useMemo(() => {
    const header = activeColumns.map((column) => column.label).join(';');
    const body = rows.map((row) => activeColumns.map((column) => row[column.key]).join(';'));
    return [header, ...body].join('\n');
  }, [activeColumns, rows]);

  const toggleColumn = (key) => {
    setVisible((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const move = (key, direction) => {
    setOrder((prev) => {
      const index = prev.indexOf(key);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  return (
    <div className="acl-frame">
      <div className="acl-toolbar">
        <div className="acl-search">
          <MagnifyingGlassIcon className="acl-search-icon" />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar en el listado"
            aria-label="Buscar en el listado de la sandbox"
          />
        </div>
        <button type="button" className="acl-btn" onClick={() => setShowCsv((prev) => !prev)}>
          <ArrowDownTrayIcon className="acl-btn-icon" /> Exportar Excel
        </button>
        <button
          type="button"
          className={`acl-btn${panelOpen ? ' is-open' : ''}`}
          onClick={() => setPanelOpen((prev) => !prev)}
          aria-expanded={panelOpen}
        >
          <AdjustmentsHorizontalIcon className="acl-btn-icon" /> Columnas
        </button>
      </div>

      {panelOpen && (
        <div className="acl-panel">
          <span className="acl-panel-title">Columnas visibles</span>
          {order.map((key, index) => {
            const column = COLUMNS.find((candidate) => candidate.key === key);
            if (!column) return null;
            return (
              <div key={key} className="acl-panel-row">
                <label className="acl-check">
                  <input
                    type="checkbox"
                    checked={visible.has(key)}
                    onChange={() => toggleColumn(key)}
                  />
                  {column.label}
                </label>
                <span className="acl-move">
                  <button type="button" onClick={() => move(key, -1)} disabled={index === 0} aria-label={`Subir ${column.label}`}>
                    <ArrowUpIcon className="acl-move-icon" />
                  </button>
                  <button type="button" onClick={() => move(key, 1)} disabled={index === order.length - 1} aria-label={`Bajar ${column.label}`}>
                    <ArrowDownIcon className="acl-move-icon" />
                  </button>
                </span>
              </div>
            );
          })}
          <p className="acl-panel-hint">En la aplicación real esta elección se recuerda para esa lista.</p>
        </div>
      )}

      <div className="acl-table-wrap">
        <table className="acl-table">
          <thead>
            <tr>
              {activeColumns.map((column) => <th key={column.key}>{column.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                {activeColumns.map((column) => <td key={column.key}>{row[column.key]}</td>)}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={Math.max(activeColumns.length, 1)} className="acl-empty">
                  Ningún registro coincide con «{query}».
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showCsv && (
        <pre className="acl-csv" aria-label="Vista previa de la exportación">{csv}</pre>
      )}

      <div className="acl-foot">
        <span>{rows.length} filas · {activeColumns.length} columnas</span>
        <button
          type="button"
          className="acl-reset"
          onClick={() => {
            setVisible(new Set(['name', 'type', 'location', 'state']));
            setOrder(COLUMNS.map((column) => column.key));
            setQuery('');
            setShowCsv(false);
            setPanelOpen(false);
          }}
        >
          Volver al estado inicial
        </button>
      </div>
    </div>
  );
}
